/**
 * WP-A (Gap B) — The product's ONLY access path to evidence.
 *
 * Cutover contract, enforced structurally by this module:
 *
 *   * every evidence record the product renders comes from a hosted authority
 *     read. Nothing is stored, cached durably, seeded, or invented locally, and
 *     nothing here reads or writes localStorage;
 *   * every step (register → upload → inspect → bind) is an authenticated server
 *     command. The browser cannot set a state, a hash, a size, a detected type, a
 *     scanner verdict, or a sufficiency;
 *   * ELIGIBILITY IS NEVER COMPUTED HERE. Whether a record may support a
 *     condition is asked of the backend predicate through
 *     `checkEvidenceSupportsCondition`. This module exposes governed facts and a
 *     backend answer, never a locally derived "looks green" judgement;
 *   * failures surface as the categorical sentences in `evidenceFailureMessage`.
 *     No provider text, SQL, object path, signed URL secret, or raw exception can
 *     reach the UI through this module.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useMemo, useState } from "react";

import {
  bindEvidenceCondition,
  checkEvidenceSupportsCondition,
  inspectEvidenceRecord,
  listLoopEvidenceRecords,
  registerEvidenceRecord,
  removeEvidenceRecord,
  requestEvidenceDownload,
  uploadEvidenceBytes,
} from "../evidence.functions";
import { evidenceFailureMessage, type EvidenceFailureReason } from "../evidenceErrors";
import type { EvidenceListEntry } from "../evidencePort";
import { MAX_EVIDENCE_BYTES } from "../objectKey";

const evidenceKey = (loopId: string) => ["hostedEvidence", loopId] as const;

export type HostedEvidencePhase = "loading" | "ready" | "unavailable";

export interface HostedEvidenceView {
  readonly phase: HostedEvidencePhase;
  /** Empty while loading or unavailable: emptiness is never mistaken for truth. */
  readonly entries: readonly EvidenceListEntry[];
  readonly message: string | null;
  readonly refresh: () => void;
}

export type EvidenceActionResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly message: string; readonly retryable: boolean };

/** The one governed upload ceiling the UI is allowed to pre-check, as a courtesy. */
export const EVIDENCE_MAX_BYTES = MAX_EVIDENCE_BYTES;

/** Types offered in the file picker. The SERVER re-decides from actual bytes. */
export const EVIDENCE_ACCEPT_ATTRIBUTE = "application/pdf,image/png,image/jpeg,text/plain";

function fail(reason: EvidenceFailureReason, retryable: boolean): EvidenceActionResult {
  return { ok: false, message: evidenceFailureMessage(reason), retryable };
}

/** Base64 for transport only. The server recomputes every authoritative fact. */
async function toBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let index = 0; index < buffer.length; index += 1) {
    binary += String.fromCharCode(buffer[index] as number);
  }
  return btoa(binary);
}

export function useHostedEvidence(loopId: string | null): HostedEvidenceView {
  const listEvidence = useServerFn(listLoopEvidenceRecords);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: evidenceKey(loopId ?? "none"),
    queryFn: () => listEvidence({ data: { loopId: loopId as string } }),
    enabled: loopId !== null,
    staleTime: 5_000,
    retry: 1,
  });

  const refresh = useCallback(() => {
    if (loopId) void client.invalidateQueries({ queryKey: evidenceKey(loopId) });
  }, [client, loopId]);

  return useMemo<HostedEvidenceView>(() => {
    if (loopId === null) return { phase: "ready", entries: [], message: null, refresh };
    if (query.isPending) return { phase: "loading", entries: [], message: null, refresh };
    const outcome = query.data;
    if (query.isError || outcome === undefined) {
      return {
        phase: "unavailable",
        entries: [],
        message: evidenceFailureMessage("temporarily_unavailable"),
        refresh,
      };
    }
    if (!outcome.ok) {
      return {
        phase: "unavailable",
        entries: [],
        message: evidenceFailureMessage(outcome.reason),
        refresh,
      };
    }
    return { phase: "ready", entries: outcome.entries, message: null, refresh };
  }, [loopId, query.isPending, query.isError, query.data, refresh]);
}

export interface HostedEvidenceActions {
  readonly busy: boolean;
  /**
   * The whole governed submission in one product gesture: register, upload the
   * bytes through the server, then ask the server to inspect and scan them. The
   * browser never decides the outcome of any step.
   */
  readonly submit: (loopId: string, file: File) => Promise<EvidenceActionResult>;
  /** Re-run inspection where the backend contract allows it (quarantine retry). */
  readonly reinspect: (evidenceId: string, loopId: string) => Promise<EvidenceActionResult>;
  readonly bindToCondition: (
    evidenceId: string,
    conditionId: string,
    loopId: string,
  ) => Promise<EvidenceActionResult>;
  readonly remove: (evidenceId: string, loopId: string) => Promise<EvidenceActionResult>;
  /** A short-lived signed URL, minted by the server after authorization. */
  readonly download: (
    evidenceId: string,
  ) => Promise<{ ok: true; url: string } | { ok: false; message: string }>;
  /** THE authoritative eligibility question. Answered by the database. */
  readonly supportsCondition: (evidenceId: string, conditionId: string) => Promise<boolean | null>;
  /** Re-read evidence and the Loop detail from authority (after any refusal). */
  readonly refresh: (loopId: string) => Promise<void>;
}

export function useHostedEvidenceActions(): HostedEvidenceActions {
  const register = useServerFn(registerEvidenceRecord);
  const upload = useServerFn(uploadEvidenceBytes);
  const inspect = useServerFn(inspectEvidenceRecord);
  const bind = useServerFn(bindEvidenceCondition);
  const remove = useServerFn(removeEvidenceRecord);
  const download = useServerFn(requestEvidenceDownload);
  const supports = useServerFn(checkEvidenceSupportsCondition);
  const client = useQueryClient();
  const [pending, setPending] = useState(0);

  const invalidate = useCallback(
    async (loopId: string) => {
      await client.invalidateQueries({ queryKey: evidenceKey(loopId) });
      // A newly accepted record can change what the Loop detail read reports.
      await client.invalidateQueries({ queryKey: ["hostedLoop", loopId] });
    },
    [client],
  );

  const submit = useCallback<HostedEvidenceActions["submit"]>(
    async (loopId, file) => {
      setPending((n) => n + 1);
      try {
        if (file.size === 0) return fail("validation_failed", false);
        if (file.size > EVIDENCE_MAX_BYTES) return fail("object_too_large", false);

        const registered = await register({
          data: {
            loopId,
            label: file.name.slice(0, 300),
            // Advisory only: recorded, never read by any acceptance decision.
            declaredMime: file.type === "" ? null : file.type,
            declaredContentHash: null,
            declaredSize: file.size,
          },
        });
        if (!registered.ok) return fail(registered.reason, registered.retryable);

        const bytesBase64 = await toBase64(file);
        const uploaded = await upload({
          data: { evidenceId: registered.record.evidenceId, bytesBase64 },
        });
        if (!uploaded.ok) return fail(uploaded.reason, uploaded.retryable);

        // Inspection is the ONLY route to `accepted`, and it is the server's.
        const inspected = await inspect({ data: { evidenceId: registered.record.evidenceId } });
        if (!inspected.ok) return fail(inspected.reason, inspected.retryable);
        return { ok: true };
      } catch {
        return fail("temporarily_unavailable", true);
      } finally {
        setPending((n) => n - 1);
        await invalidate(loopId);
      }
    },
    [inspect, invalidate, register, upload],
  );

  const reinspect = useCallback<HostedEvidenceActions["reinspect"]>(
    async (evidenceId, loopId) => {
      setPending((n) => n + 1);
      try {
        const inspected = await inspect({ data: { evidenceId } });
        return inspected.ok ? { ok: true } : fail(inspected.reason, inspected.retryable);
      } catch {
        return fail("temporarily_unavailable", true);
      } finally {
        setPending((n) => n - 1);
        await invalidate(loopId);
      }
    },
    [inspect, invalidate],
  );

  const bindToCondition = useCallback<HostedEvidenceActions["bindToCondition"]>(
    async (evidenceId, conditionId, loopId) => {
      setPending((n) => n + 1);
      try {
        const bound = await bind({ data: { evidenceId, conditionId } });
        return bound.ok ? { ok: true } : fail(bound.reason, bound.retryable);
      } catch {
        return fail("temporarily_unavailable", true);
      } finally {
        setPending((n) => n - 1);
        await invalidate(loopId);
      }
    },
    [bind, invalidate],
  );

  const removeRecord = useCallback<HostedEvidenceActions["remove"]>(
    async (evidenceId, loopId) => {
      setPending((n) => n + 1);
      try {
        const removed = await remove({ data: { evidenceId } });
        return removed.ok ? { ok: true } : fail(removed.reason, removed.retryable);
      } catch {
        return fail("temporarily_unavailable", true);
      } finally {
        setPending((n) => n - 1);
        await invalidate(loopId);
      }
    },
    [invalidate, remove],
  );

  const requestDownload = useCallback<HostedEvidenceActions["download"]>(
    async (evidenceId) => {
      try {
        const outcome = await download({ data: { evidenceId } });
        if (!outcome.ok) return { ok: false, message: evidenceFailureMessage(outcome.reason) };
        return { ok: true, url: outcome.url };
      } catch {
        return { ok: false, message: evidenceFailureMessage("temporarily_unavailable") };
      }
    },
    [download],
  );

  const supportsCondition = useCallback<HostedEvidenceActions["supportsCondition"]>(
    async (evidenceId, conditionId) => {
      try {
        const outcome = await supports({ data: { evidenceId, conditionId } });
        // `null` means "the authority did not answer", which is NOT eligibility.
        return outcome.ok ? outcome.supports : null;
      } catch {
        return null;
      }
    },
    [supports],
  );

  return useMemo<HostedEvidenceActions>(
    () => ({
      busy: pending > 0,
      submit,
      reinspect,
      bindToCondition,
      remove: removeRecord,
      download: requestDownload,
      supportsCondition,
      refresh: invalidate,
    }),
    [
      bindToCondition,
      invalidate,
      pending,
      reinspect,
      removeRecord,
      requestDownload,
      submit,
      supportsCondition,
    ],
  );
}

export {
  evidenceProductState,
  evidenceRetryAllowed,
  evidenceStateCopy,
  type EvidenceProductState,
} from "./evidenceProjection";
